import axios from "axios";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { getRuntimeEnv } from "@/lib/runtimeEnv";
import { broadcastAuthLoggedOut } from "@/lib/authSync";
import { normalizeApiErrorDetail } from "@/lib/apiError";
import { getTenantSubdomain } from "@/lib/tenant";

declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthHeader?: boolean;
    authTokenAtRequest?: string | null;
  }

  export interface InternalAxiosRequestConfig {
    skipAuthHeader?: boolean;
    authTokenAtRequest?: string | null;
  }
}

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, "");

const resolveApiUrl = () => {
  const configured = getRuntimeEnv("VITE_API_URL")?.trim();

  // If configured as localhost but app is on a localhost subdomain (e.g. acme.localhost),
  // rewrite the API host to preserve tenant host resolution in local multi-tenant testing.
  if (configured && typeof window !== "undefined") {
    const currentHost = window.location.hostname;
    if (currentHost.endsWith(".localhost")) {
      try {
        const parsed = new URL(configured);
        if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
          parsed.hostname = currentHost;
          return trimTrailingSlash(parsed.toString());
        }
      } catch {
        // Keep configured value unchanged when URL parsing fails.
      }
    }
    return trimTrailingSlash(configured);
  }

  // Production default: same-origin /api via reverse proxy.
  // Local fallback: direct API on same hostname at port 8000.
  if (typeof window !== "undefined") {
    const { protocol, hostname } = window.location;
    if (hostname === "localhost" || hostname === "127.0.0.1" || hostname.endsWith(".localhost")) {
      return `${protocol}//${hostname}:8000`;
    }
  }

  return "";
};

export const API_BASE_URL = resolveApiUrl();

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    config.authTokenAtRequest = token;
    if (token && !config.skipAuthHeader) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const normalizedDetail = normalizeApiErrorDetail(error.response?.data?.detail);
    if (normalizedDetail && error.response?.data && typeof error.response.data === "object") {
      const structured = error.response.data.detail;
      if (structured && typeof structured === "object") {
        error.response.data.detailPayload = structured;
      }
      error.response.data.detail = normalizedDetail;
    }
    if (
      normalizedDetail &&
      (!error.message || /^Request failed with status code \d+$/.test(error.message))
    ) {
      error.message = normalizedDetail;
    }

    // Handle 401 Unauthorized
    if (error.response && error.response.status === 401) {
      const { logout, token } = useAuthStore.getState();
      const tokenAtRequest = error.config?.authTokenAtRequest ?? null;
      if (token === tokenAtRequest) {
        logout();
        broadcastAuthLoggedOut();
      }
    }

    if (error.response?.status === 403 && getTenantSubdomain(window.location.hostname)) {
      void import("@/lib/tenantRecovery").then(({ isWorkspaceError, recoverFromInvalidTenant }) => {
        if (isWorkspaceError(normalizedDetail)) {
          void recoverFromInvalidTenant({ reason: "wrong_workspace" });
        }
      });
    }

    // Handle 500 Server Errors globally (optional, but good for UX)
    if (error.response && error.response.status >= 500) {
      toast.error("Server Error", {
        description: "Something went wrong on the server. Please try again later.",
      });
    }

    return Promise.reject(error);
  }
);
